import { useEffect, useState } from "react";
import { api, GBP } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { BarChart, Bar, XAxis, ResponsiveContainer, Cell } from "recharts";

const RANGES = [["this_month", "This Month"], ["last_month", "Last Month"], ["this_quarter", "This Quarter"], ["this_year", "This Year"]];

export default function Financials() {
  const [range, setRange] = useState("this_month");
  const [d, setD] = useState(null);

  useEffect(() => { api.get("/financials", { params: { range } }).then((r) => setD(r.data)).catch(() => {}); }, [range]);
  if (!d) return <div className="text-[#8C867A]">Loading…</div>;

  const revenue = [
    { name: "Membership", value: d.membership_revenue, color: "#D4AF37" },
    { name: "Trade", value: d.trade_revenue, color: "#C5A059" },
    { name: "Education", value: d.education_revenue, color: "#8C867A" },
    { name: "Other", value: d.other_revenue, color: "#5c5850" },
  ];

  return (
    <div>
      <PageHeader eyebrow="Operational Dashboard" title="Financial Overview" />
      <div className="flex gap-2 mb-6 overflow-x-auto">
        {RANGES.map(([k, l]) => (
          <button key={k} onClick={() => setRange(k)} data-testid={`range-${k}`}
            className={`whitespace-nowrap px-3.5 py-2 rounded-sm text-xs uppercase tracking-wider border transition-all ${range === k ? "border-[#C5A059] bg-[#1A1A1E] text-[#F5F3EF]" : "hairline text-[#8C867A]"}`}>{l}</button>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <div className="lg:col-span-2 panel rounded-sm p-6 rise">
          <div className="eyebrow mb-1">Total Revenue</div>
          <div className="font-serif text-5xl brass-text mb-6">{GBP(d.total_revenue)}</div>
          <ResponsiveContainer width="100%" height={140}>
            <BarChart data={revenue}>
              <XAxis dataKey="name" tick={{ fill: "#8C867A", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>{revenue.map((e, i) => <Cell key={i} fill={e.color} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t hairline">
            {revenue.map((r) => (
              <div key={r.name}><div className="eyebrow mb-1">{r.name}</div><div className="font-mono text-sm text-[#E2DFD7]">{GBP(r.value)}</div></div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="panel rounded-sm p-5">
            <div className="eyebrow mb-3">Trade Profitability</div>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between"><span className="text-[#8C867A]">Trade Revenue</span><span className="font-mono text-[#E2DFD7]">{GBP(d.trade_revenue)}</span></div>
              <div className="flex justify-between"><span className="text-[#8C867A]">Direct Costs</span><span className="font-mono text-[#E2DFD7]">{GBP(d.trade_direct_costs)}</span></div>
              <div className="flex justify-between pt-2 border-t hairline"><span className="text-[#8C867A]">Gross Profit</span><span className="font-mono" style={{ color: "#34D399" }}>{GBP(d.gross_profit)}</span></div>
              <div className="flex justify-between"><span className="text-[#8C867A]">Gross Margin</span><span className="font-mono brass-text">{d.gross_margin.toFixed(1)}%</span></div>
            </div>
          </div>
          <div className="panel rounded-sm p-5">
            <div className="eyebrow mb-2">Outstanding Payments</div>
            <div className="font-serif text-2xl" style={{ color: d.outstanding_payments ? "#F59E0B" : "#F5F3EF" }}>{GBP(d.outstanding_payments)}</div>
          </div>
        </div>
      </div>

      <div className="panel rounded-sm p-5 flex flex-wrap items-center justify-between gap-4" style={{ borderColor: "#3E382A" }}>
        <div>
          <div className="eyebrow mb-1">Deposit Liabilities — Held in Trust</div>
          <div className="font-serif text-3xl brass-text">{GBP(d.deposit_liabilities)}</div>
        </div>
        <div className="text-xs text-[#8C867A] max-w-sm">Security deposits are recorded as a liability and deliberately excluded from all revenue figures. This is an operational dashboard, not accounting software.</div>
      </div>
    </div>
  );
}
